import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useMemberProducts } from "@nju/data-access-portal-product";
import { ErrorMessage } from "@nju/form";
import { Checkbox } from "@nju/form";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import {
  A,
  Button,
  GeneralErrorNotification,
  Panel,
  Separator,
  Text,
} from "@nju/ui";
import { mySimCardSettingsMachine } from "./mySimCardSettings.machine";
import { MySimCardSettingsSuccess } from "./MySimCardSettingsSuccess";

export function MySimCardSettings() {
  const router = useRouter();
  const params = portalRoutes.mojeKonto.mojaKarta.ustawieniaNumeru
    .input(router.query)
    .unwrap();
  const { mobileProducts } = useMemberProducts();
  const productId = mobileProducts.mobileAccess?.id;

  const voiceMailStatus =
    mobileProducts.mobileAccess?.characteristic?.voiceMailLostCalls === "VM";
  const acrStatus = mobileProducts.mobileAccess?.characteristic?.acr === "Y";
  const roamingStatus =
    mobileProducts.mobileAccess?.characteristic?.roaming === "Y";
  const isRoamingBarringActive =
    mobileProducts.mobileAccess?.characteristic?.fraudRoamingBarring === "Y";

  const [current, send] = useMachine(mySimCardSettingsMachine, {
    context: {
      productId,
      isAcrActive: acrStatus,
      isRoamingActive: roamingStatus,
      isVoiceMailActive: voiceMailStatus,
      initialSettings: {
        isAcrActive: acrStatus,
        isRoamingActive: roamingStatus,
        isVoiceMailActive: voiceMailStatus,
      },
    },
  });
  const { context, matches } = current;

  const { isAcrActive, isRoamingActive, isVoiceMailActive, formError } =
    context;

  const setSimCardSettings = React.useCallback(() => {
    send({ type: "SAVE_SETTINGS" });
  }, [send]);

  const toggleAcrStatus = React.useCallback(
    (value: boolean) => {
      send({
        type: "SET_ACR",
        value,
      });
    },
    [send],
  );

  const toggleRoamingStatus = React.useCallback(
    (value: boolean) => {
      send({
        type: "SET_ROAMING",
        value,
      });
    },
    [send],
  );

  const toggleVoiceMailStatus = React.useCallback(
    (value: boolean) => {
      send({
        type: "SET_VOICE_MAIL",
        value,
      });
    },
    [send],
  );

  if (!mobileProducts) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  if (matches("success")) {
    return <MySimCardSettingsSuccess userId={params?.userId} />;
  }

  if (matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return window.location.reload();
        }}
      />
    );
  }

  return (
    <Panel testId="mobile-number-settings-panel">
      <div className="flex items-center">
        <Icon height={32} name="sliders-h" tone="accent" width={32} />
        <Text className="pl-3" size="xl" weight="bold">
          ustawienia numeru
        </Text>
      </div>
      <Separator size="s" />
      <Checkbox
        checkboxPosition="center"
        height={12}
        isSelected={isVoiceMailActive}
        name="voiceMail"
        onValueChange={toggleVoiceMailStatus}
        width={20}
      >
        <div className="flex flex-col ml-3">
          <Text
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "l",
            }}
            weight="bold"
          >
            poczta głosowa
          </Text>
          <Text
            size={{
              "@onlyMobile": "xs",
              "@fromTablet": "s",
            }}
          >
            {isVoiceMailActive ? "włączona" : "wyłączona"}
          </Text>
        </div>
      </Checkbox>
      <Separator size="s" />
      <Checkbox
        checkboxPosition="center"
        height={12}
        isSelected={isAcrActive}
        name="acr"
        onValueChange={toggleAcrStatus}
        width={20}
      >
        <div className="flex flex-col ml-3">
          <Text
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "l",
            }}
            weight="bold"
          >
            odrzucenie połączeń z numerów zastrzeżonych
          </Text>
          <Text
            size={{
              "@onlyMobile": "xs",
              "@fromTablet": "s",
            }}
          >
            {isAcrActive ? "włączone" : "wyłączone"}
          </Text>
        </div>
      </Checkbox>
      <Separator size="s" />
      <Checkbox
        checkboxPosition="center"
        height={12}
        isDisabled={isRoamingBarringActive}
        isDisabledBackground={isRoamingBarringActive}
        isSelected={isRoamingActive}
        name="roaming"
        onValueChange={toggleRoamingStatus}
        width={20}
      >
        <div className="flex flex-col ml-3">
          <Text
            size={{
              "@onlyMobile": "s",
              "@fromTablet": "l",
            }}
            weight="bold"
          >
            roaming
          </Text>
          <Text
            size={{
              "@onlyMobile": "xs",
              "@fromTablet": "s",
            }}
          >
            {!isRoamingBarringActive &&
              (isRoamingActive ? "włączony" : "wyłączony")}
            {isRoamingBarringActive && "zablokowany"}
          </Text>
        </div>
      </Checkbox>
      <Separator size="s" tone="invisible" />
      {formError && (
        <>
          <ErrorMessage message={formError} />
          <Separator size="s" tone="invisible" />
        </>
      )}
      <div className="flex gap-4 only-mobile:gap-3 only-mobile:flex-col-reverse flex-row">
        <Link
          href={portalRoutes.mojeKonto.mojaKarta.index.get({
            ...(params?.userId && { userId: params.userId }),
          })}
          passHref={true}
        >
          <A
            size={{
              "@onlyMobile": "s",
            }}
            variant="secondary"
          >
            anuluj
          </A>
        </Link>
        <Button
          isLoading={matches("setSimCardSettings")}
          onPress={setSimCardSettings}
          size={{ "@onlyMobile": "s" }}
        >
          zapisz zmiany
        </Button>
      </div>
    </Panel>
  );
}
